<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. OVERVIEW & PLATFORM MODEL</h2>
            <p class="text-body mb-2">1.1 ILearn’d Tutors operates on a <b>charge-after-class payment model</b> for tutoring sessions.</p>
            <p class="text-body mb-2">1.2 Students are <b>not charged in advance</b> for individual tutoring sessions. Payment is automatically deducted <b>only after successful completion of a session,</b> in accordance with the Payment Terms & Conditions.</p>
            <p class="text-body mb-2">1.3 As a result, refunds generally apply <b>only in limited circumstances where:</b></p>
            <p class="text-body ml-3 mb-1">a payment has already been processed due to system error; or</p>
            <p class="text-body ml-3 mb-2">a charge was incorrectly applied contrary to this Policy.</p>
            <p class="text-body mb-2">1.4 All bookings, cancellations, disputes, and refunds must be processed <b>exclusively through the ILearn’d Tutors Platform.</b></p>
            <p class="text-body">1.5 ILearn’d Tutors bears <b>no responsibility</b> for any payments or sessions arranged outside the Platform.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. ELIGIBILITY FOR REFUNDS</h2>
            <p class="text-body mb-2">2.1 A user may be eligible for a refund <b>only where a payment has already been deducted,</b> and one of the following applies:</p>
            <p class="text-body ml-3 mb-1">(a) the tutor failed to attend the scheduled session;</p>
            <p class="text-body ml-3 mb-1">(b) a verified Platform-related technical failure prevented the session from taking place;</p>
            <p class="text-body ml-3 mb-1">(c) an incorrect or duplicate charge was applied due to a system error;</p>
            <p class="text-body ml-3 mb-2">(d) a charge was applied despite a valid cancellation in accordance with this Policy.</p>
            <p class="text-body">2.2 Refunds shall <b>not</b> be issued for personal reasons unrelated to Platform failure, tutor non-attendance, or verified payment error.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. STUDENT-INITIATED CANCELLATIONS</h2>
            <p class="text-body font-bold mb-1">3.1 Cancellation Before Payment Is Triggered</p>
            <p class="text-body mb-2">3.1.1 Where a student cancels a session:</p>
            <p class="text-body ml-3 mb-1">(a) <b>24 hours or more</b> before the scheduled start time <b>– no charge is applied;</b></p>
            <p class="text-body ml-3 mb-1">(b) <b>2 to 24 hours</b> before the scheduled start time <b>– no charge is applied;</b></p>
            <p class="text-body ml-3 mb-2">(c) <b>less than 2 hours</b> before the scheduled start time or in the event of a no-show – the Platform <b>may apply the full session charge.</b></p>
            <p class="text-body mb-3">3.1.2 Where no charge has been applied, <b>no refund is necessary.</b></p>

            <p class="text-body font-bold mb-1">3.2 Exceptional Circumstances</p>
            <p class="text-body mb-2">3.2.1 ILearn’d Tutors may, at its discretion, consider refund requests made outside the standard timeframes where the student demonstrates circumstances beyond their reasonable control, including but not limited to:</p>
            <p class="text-body ml-3 mb-1">(a) sudden illness or medical emergency;</p>
            <p class="text-body ml-3 mb-1">(b) natural disasters;</p>
            <p class="text-body ml-3 mb-1">(c) power outages or widespread network failures; or</p>
            <p class="text-body ml-3 mb-2">(d) other force majeure events.</p>
            <p class="text-body">3.2.2 Supporting evidence may be requested.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. TUTOR-INITIATED CANCELLATIONS & NO-SHOWS</h2>
            <p class="text-body mb-2">4.1 Where a tutor cancels a session at any time, or fails to attend a scheduled session:</p>
            <p class="text-body ml-3 mb-1">(a) <b>no charge shall be applied</b> to the student; or</p>
            <p class="text-body ml-3 mb-2">(b) where a charge has already been applied in error, a <b>full refund shall be issued.</b></p>
            <p class="text-body mb-2">4.2 Tutors who repeatedly cancel or fail to attend sessions may be subject to:</p>
            <p class="text-body ml-3 mb-1">(a) warnings;</p>
            <p class="text-body ml-3 mb-1">(b) reduced platform visibility;</p>
            <p class="text-body ml-3 mb-1">(c) financial penalties;</p>
            <p class="text-body ml-3 mb-1">(d) temporary suspension; or</p>
            <p class="text-body ml-3">(e) permanent account termination.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. TECHNICAL FAILURES</h2>
            <p class="text-body mb-2">5.1 A refund may be issued where:</p>
            <p class="text-body ml-3 mb-1">(a) there is verified evidence of a <b>Platform-related technical failure;</b></p>
            <p class="text-body ml-3 mb-1">(b) the session could not reasonably commence or continue; and</p>
            <p class="text-body ml-3 mb-2">(c) both parties made reasonable attempts to resolve the issue.</p>
            <p class="text-body mb-2">5.2 The following <b>do not qualify</b> for refunds:</p>
            <p class="text-body ml-3 mb-1">(a) student’s personal internet failure;</p>
            <p class="text-body ml-3 mb-1">(b) device malfunction on the user’s side; and</p>
            <p class="text-body ml-3">(c) failure to prepare or log in on time without Platform error.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. NO-SHOW POLICY</h2>
            <p class="text-body font-bold mb-1">6.1 Student No-Show</p>
            <p class="text-body mb-1">Where a student fails to attend without valid cancellation:</p>
            <p class="text-body ml-3 mb-1">the full session charge may be applied;</p>
            <p class="text-body ml-3 mb-3">no refund shall be issued.</p>

            <p class="text-body font-bold mb-1">6.2 Tutor No-Show</p>
            <p class="text-body mb-1">Where a tutor fails to attend:</p>
            <p class="text-body ml-3 mb-1">no charge shall apply to the student; or</p>
            <p class="text-body ml-3">a full refund shall be issued if charged in error.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. NON-REFUNDABLE SITUATIONS</h2>
            <p class="text-body mb-2">Refunds shall <b>not</b> be issued for:</p>
            <p class="text-body ml-3 mb-1">(a) off-platform payments or arrangements;</p>
            <p class="text-body ml-3 mb-1">(b) dissatisfaction with teaching style after session completion;</p>
            <p class="text-body ml-3 mb-1">(c) failure to attend without cancellation;</p>
            <p class="text-body ml-3 mb-1">(d) misuse of the Platform or breach of policies;</p>
            <p class="text-body ml-3 mb-1">(e) subscription fees once the billing cycle has commenced;</p>
            <p class="text-body ml-3 mb-1">(f) partially used subscription periods;</p>
            <p class="text-body ml-3 mb-1">(g) digital content, past papers, or learning materials once accessed; and</p>
            <p class="text-body ml-3">(h) promotional or discounted subscription periods already used.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. SUBSCRIPTIONS & PACKAGES</h2>
            <p class="text-body mb-2">8.1 Subscription fees are billed on a recurring basis and are <b>non-refundable once the billing period has commenced,</b> except where required by law.</p>
            <p class="text-body mb-2">8.2 Where multi-session packages are offered:</p>
            <p class="text-body ml-3 mb-1">(a) completed sessions are non-refundable;</p>
            <p class="text-body ml-3 mb-1">(b) refunds, if any, apply only to unused sessions;</p>
            <p class="text-body ml-3 mb-2">(c) promotional discounts may be recalculated prior to refund.</p>
            <p class="text-body">8.3 Refunds are not provided for unused AI-generated content or transcripts that expire or are deleted in accordance with the Platform’s transcript retention policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. FRAUD, MISUSE & ABUSE OF REFUNDS</h2>
            <p class="text-body mb-2">9.1 ILearn’d Tutors reserves the right to:</p>
            <p class="text-body ml-3 mb-1">(a) withhold refunds where fraud or misuse is suspected;</p>
            <p class="text-body ml-3 mb-1">(b) investigate repeated or abusive refund requests;</p>
            <p class="text-body ml-3 mb-1">(c) suspend or terminate accounts engaged in refund abuse; and</p>
            <p class="text-body ml-3">(d) report suspected fraudulent activity to relevant authorities.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. OFF-PLATFORM BOOKINGS DISCLAIMER</h2>
            <p class="text-body mb-2">10.1 ILearn’d Tutors is not responsible for:</p>
            <p class="text-body ml-3 mb-1">(a) payments made privately or directly;</p>
            <p class="text-body ml-3 mb-1">(b) sessions arranged outside the Platform;</p>
            <p class="text-body ml-3 mb-2">(c) disputes, losses, or misconduct arising from off-platform arrangements.</p>
            <p class="text-body">10.2 Such transactions are <b>not eligible for refunds under any circumstances.</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. REFUND REQUEST PROCESS</h2>
            <p class="text-body mb-2">11.1 Refund or dispute requests should be submitted <b>as soon as reasonably practicable,</b> and ordinarily within <b>forty-eight (48) hours</b> of the session, unless circumstances beyond the user’s control prevent timely submission.</p>
            <p class="text-body mb-2">11.2 Requests must be submitted through the Platform’s official complaints or support system and include relevant details.</p>
            <p class="text-body">11.3 ILearn’d Tutors shall review and respond within a reasonable timeframe.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. REFUND PROCESSING TIME</h2>
            <p class="text-body mb-2">12.1 Approved refunds shall be processed within <b>five (5) to seven (7) business days.</b></p>
            <p class="text-body mb-2">12.2 Actual credit timing may vary depending on banks or payment service providers.</p>
            <p class="text-body">12.3 Refunds shall be issued <b>only to the original payment method</b> used.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. GOVERNING LAW</h2>
            <p class="text-body">13.1 This Refund Policy shall be governed by the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b></p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">14. CONTACT INFORMATION</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1"><b>ILearn’d Tutors – Legal Team</b></p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }
</style>